import { ReactElement, ReactNode } from 'react';

import { getComponentData, useElementFilter } from '@backstage/core-plugin-api';

import { PageRouteRef } from '../utils';

export const PAGE_CONTENT_KEY = 'page.content';
export const PAGE_CONTENT_SIDEBAR_LEFT_KEY = 'page.content.sideBarLeft';
export const PAGE_CONTENT_SIDEBAR_RIGHT_KEY = 'page.content.sideBarRight';
export const PAGE_CONTENT_ACTION_BAR_KEY = 'page.content.actionBar';
export const PAGE_CONTENT_TABS_KEY = 'page.content.tabs';
export const PAGE_CONTENT_MAIN_KEY = 'page.content.main';

export type PageContentActionBarProps = { children?: ReactNode };

export type PageContentTabsProps = { routeRefs: PageRouteRef[] };

export type PageContentChildren = {
  sideBarLeft: ReactNode;
  sideBarRight: ReactNode;
  actionBar: ReactNode;
  tabsRouteRefs: PageRouteRef[] | null;
  mainChildren: ReactNode[];
};

type SlotKind = 'sideBarLeft' | 'sideBarRight' | 'actionBar' | 'tabs';
type ChildKind = SlotKind | 'main' | 'unwrapped';

const KIND_BY_KEY: ReadonlyArray<
  readonly [string, Exclude<ChildKind, 'unwrapped'>]
> = [
  [PAGE_CONTENT_SIDEBAR_LEFT_KEY, 'sideBarLeft'],
  [PAGE_CONTENT_SIDEBAR_RIGHT_KEY, 'sideBarRight'],
  [PAGE_CONTENT_ACTION_BAR_KEY, 'actionBar'],
  [PAGE_CONTENT_TABS_KEY, 'tabs'],
  [PAGE_CONTENT_MAIN_KEY, 'main'],
];

const SLOT_KINDS: ReadonlySet<ChildKind> = new Set<SlotKind>([
  'sideBarLeft',
  'sideBarRight',
  'actionBar',
  'tabs',
]);

const getChildKind = (element: ReactElement): ChildKind => {
  for (const [key, kind] of KIND_BY_KEY) {
    if (getComponentData<boolean>(element, key)) return kind;
  }
  return 'unwrapped';
};

const assertValidComposition = (kinds: readonly ChildKind[]): void => {
  const hasSlot = kinds.some(kind => SLOT_KINDS.has(kind));
  const hasUnwrapped = kinds.includes('unwrapped');

  if (hasSlot && hasUnwrapped) {
    throw new Error(
      '<Page.Content> mixes slot children (Page.ContentActionBar, ' +
        'Page.ContentSideBarLeft, Page.ContentSideBarRight, Page.ContentTabs) ' +
        'with unwrapped content. Wrap the main content in <Page.ContentMain>. ' +
        'A single, unwrapped main child is only allowed when there are no ' +
        'slot siblings.',
    );
  }
};

export const usePageContentChildren = (
  children: ReactNode,
): PageContentChildren => {
  return useElementFilter(children, elements => {
    const classified = elements
      .getElements<{ children: ReactNode; routeRefs?: PageRouteRef[] }>()
      .map(element => ({ element, kind: getChildKind(element) }));

    if (process.env.NODE_ENV === 'development') {
      assertValidComposition(classified.map(({ kind }) => kind));
    }

    let sideBarLeft: ReactNode = null;
    let sideBarRight: ReactNode = null;
    let actionBar: ReactNode = null;
    let tabsRouteRefs: PageRouteRef[] | null = null;
    const mainChildren: ReactNode[] = [];

    classified.forEach(({ element, kind }) => {
      switch (kind) {
        case 'sideBarLeft':
          sideBarLeft = element.props.children;
          break;
        case 'sideBarRight':
          sideBarRight = element.props.children;
          break;
        case 'actionBar':
          actionBar = element.props.children;
          break;
        case 'tabs':
          tabsRouteRefs = element.props.routeRefs ?? [];
          break;
        case 'main':
        case 'unwrapped':
          mainChildren.push(element);
          break;
        default: {
          const exhaustive: never = kind;
          throw new Error(`Unhandled Page.Content child kind: ${exhaustive}`);
        }
      }
    });

    return {
      sideBarLeft,
      sideBarRight,
      actionBar,
      tabsRouteRefs,
      mainChildren,
    };
  });
};
